import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import ScreenerList from './pages/ScreenerList';
import Builder from './pages/Builder';
import SpecBuilder from './pages/SpecBuilder';
import Backtest from './pages/Backtest';
import Clustering from './pages/Clustering';
import Signals from './pages/Signals';
import HelmList from './pages/HelmList';
import HelmDetail from './pages/HelmDetail';
import HelmRunResult from './pages/HelmRunResult';
import HelmWFResult from './pages/HelmWFResult';
import WfConfigCompare from './pages/WfConfigCompare';
import Dashboard from './pages/Dashboard';
import LiveList from './pages/LiveList';
import LiveDetail from './pages/LiveDetail';
import LiveAuthCallback from './pages/LiveAuthCallback';
import StockAnalysis from './pages/StockAnalysis';
import University from './pages/University';
import UniversityCourse from './pages/UniversityCourse';
import UniversityQuiz from './pages/UniversityQuiz';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Admin from './pages/Admin';
import AdminApproval from './pages/AdminApproval';
import AdminAnalytics from './pages/AdminAnalytics';
import Landing from './pages/Landing';
import GoogleAuthCallback from './pages/GoogleAuthCallback';
import VersionWatcher from './components/VersionWatcher';
import './App.css';

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: 'hsl(var(--card))',
              color: 'hsl(var(--card-foreground))',
              border: '1px solid hsl(var(--border))',
              fontSize: '0.875rem',
            },
            success: { iconTheme: { primary: 'hsl(var(--primary))', secondary: 'hsl(var(--card))' } },
            error: { iconTheme: { primary: '#f43f5e', secondary: 'hsl(var(--card))' } },
          }}
        />
        <VersionWatcher />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/auth/callback" element={<GoogleAuthCallback />} />
          <Route path="/admin" element={
            <ProtectedRoute><Admin /></ProtectedRoute>
          }>
            <Route index element={<Navigate to="/admin/analytics" replace />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="users" element={<AdminApproval />} />
            {/* The approvals page became the Users section. */}
            <Route path="approvals" element={<Navigate to="/admin/users" replace />} />
          </Route>
          <Route path="/dashboard" element={
            <ProtectedRoute><Dashboard /></ProtectedRoute>
          } />
          <Route path="/screeners" element={
            <ProtectedRoute><ScreenerList /></ProtectedRoute>
          } />
          <Route path="/builder/:id" element={
            <ProtectedRoute><Builder /></ProtectedRoute>
          } />
          <Route path="/spec/:id" element={
            <ProtectedRoute><SpecBuilder /></ProtectedRoute>
          } />
          <Route path="/signals/:id" element={
            <ProtectedRoute><Signals /></ProtectedRoute>
          } />
          <Route path="/backtest/:id" element={
            <ProtectedRoute><Backtest /></ProtectedRoute>
          } />
          <Route path="/cluster" element={
            <ProtectedRoute><Clustering /></ProtectedRoute>
          } />
          <Route path="/helms" element={
            <ProtectedRoute><HelmList /></ProtectedRoute>
          } />
          <Route path="/helms/:id" element={
            <ProtectedRoute><HelmDetail /></ProtectedRoute>
          } />
          <Route path="/helms/:id/backtest" element={
            <ProtectedRoute><HelmDetail tab="backtesting" /></ProtectedRoute>
          } />
          <Route path="/helms/:id/runs/:runId" element={
            <ProtectedRoute><HelmRunResult /></ProtectedRoute>
          } />
          <Route path="/helms/:id/wf-runs/:wfRunId" element={
            <ProtectedRoute><HelmWFResult /></ProtectedRoute>
          } />
          <Route path="/wf-configs/:configId" element={
            <ProtectedRoute><WfConfigCompare /></ProtectedRoute>
          } />
          <Route path="/paper" element={<Navigate to="/live" replace />} />
          <Route path="/paper/:id" element={<Navigate to="/live" replace />} />
          <Route path="/live" element={
            <ProtectedRoute><LiveList /></ProtectedRoute>
          } />
          <Route path="/live/auth/callback" element={
            <ProtectedRoute><LiveAuthCallback /></ProtectedRoute>
          } />
          <Route path="/live/:id" element={
            <ProtectedRoute><LiveDetail /></ProtectedRoute>
          } />
          <Route path="/stock/:symbol" element={
            <ProtectedRoute><StockAnalysis /></ProtectedRoute>
          } />
          <Route path="/university" element={
            <ProtectedRoute><University /></ProtectedRoute>
          } />
          <Route path="/university/:course" element={
            <ProtectedRoute><UniversityCourse /></ProtectedRoute>
          } />
          <Route path="/university/:course/quiz" element={
            <ProtectedRoute><UniversityQuiz /></ProtectedRoute>
          } />
          <Route path="/university/:course/:lesson" element={
            <ProtectedRoute><UniversityCourse /></ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
